import { logger } from "@/utils/logger";
import { validateOpenAIResponse, handleInvalidResponse, createInvalidResponseError } from './responseValidator';
import { deduplicateYouTubeVideos } from './youtubeDeduplication';
import SecureStorage from './secureStorage';
import { sessionManager } from './sessionManager';
import { supabase } from '../integrations/supabase/client';
import { normalizeAIResponse } from './normalizeAIResponse';

export interface AIResponse {
  'Reply_Message': string | StructuredReplyMessage | any;
  'Form'?: 'Contact' | 'FinanceV1' | 'Finance' | 'Trade' | 'Service' | 'Offer' | 'Offers' | 'Appointment' | 'None' | {
    Type?: string;
    Title?: string;
    Contact?: {
      Name?: string;
      Phone?: string;
      Email?: string;
    };
  };
  'FormTitle'?: string; // Keep for backward compatibility
  'Language'?: 'fr' | 'en' | { Current?: 'fr' | 'en' | string };
  'Contact'?: {
    Name?: string;
    First_Name?: string;
    Last_Name?: string;
    Phone?: string;
    Email?: string;
  };
  'Interest'?: any; // Interest data from AI response
  'reply_options'?: string[];
  'Reply_Options'?: Array<{ option?: string; Option?: string }> | Record<string, string>;
  'Answer'?: {
    'Conversation_Summary'?: string;
    'Email_or_Phone'?: boolean;
  };
}

export interface StructuredReplyMessage {
  Intro?: string;
  Content?: {
    Inventory?: VehicleCard[];
    Vehicle_Count?: InventoryCountItem[];
  };
  Closing?: string;
  Inventory_Count_By_Category?: InventoryCountItem[];
  Inventory_Count_By_Color?: InventoryCountItem[];
}

export interface VehicleCard {
  Main_Picture_URL?: string;
  Stock_Number?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  Trim?: string;
  "Exterior Color"?: string;
  Price?: number;
  Mileage?: number;
  VDP_URL?: string;
  Vehicle_Description_Summary?: string;
  Features?: string[];
  CARFAX_URL?: string;
  Status?: string; // New, Used, Demo (or Neuf, Occasion, Démo)
}

export interface InventoryCountItem {
  Category?: string;
  Color?: string;
  Count: number;
}

const getLastUserQuestionFromSessionStorage = (): string | null => {
  try {
    const storedMessages = sessionStorage.getItem('d2c_chat_messages');
    if (!storedMessages) return null;

    const messages = JSON.parse(storedMessages);

    const lastUserMsg = Array.isArray(messages)
      ? [...messages]
          .reverse()
          .find((m: any) => m?.role === 'user' && typeof m?.content === 'string' && m.content.trim())
      : null;

    return lastUserMsg?.content?.trim() || null;
  } catch {
    return null;
  }
};

// NEW: Robust JSON extraction function to handle OpenAI hallucinations
const extractCleanJSON = (text: string): string => {
  logger.log('🔧 EXTRACTING CLEAN JSON - Original text length:', text.length);
  logger.log('🔧 EXTRACTING CLEAN JSON - First 500 chars:', text.substring(0, 500));
  
  // STEP 1: First strip markdown code blocks if present
  let cleanedText = text;
  
  // Remove markdown code blocks - try multiple patterns
  // Pattern 1: ```json (with optional whitespace and newlines)
  let codeBlockMatch = cleanedText.match(/```json\s*\n?([\s\S]*?)\n?\s*```/);
  if (codeBlockMatch) {
    cleanedText = codeBlockMatch[1].trim();
    logger.log('🔧 EXTRACTING CLEAN JSON - Stripped ```json markdown, new length:', cleanedText.length);
  } else {
    // Pattern 2: ``` (without json specifier)
    codeBlockMatch = cleanedText.match(/```\s*\n?([\s\S]*?)\n?\s*```/);
    if (codeBlockMatch) {
      cleanedText = codeBlockMatch[1].trim();
      logger.log('🔧 EXTRACTING CLEAN JSON - Stripped ``` markdown, new length:', cleanedText.length);
    }
  }
  
  // STEP 2: Find the first opening bracket and last closing bracket
  const firstBracket = cleanedText.indexOf('{');
  const lastBracket = cleanedText.lastIndexOf('}');
  
  if (firstBracket === -1 || lastBracket === -1 || firstBracket >= lastBracket) {
    logger.log('🔧 EXTRACTING CLEAN JSON - No valid JSON brackets found');
    return text; // Return original if no valid brackets found
  }
  
  // STEP 3: Extract everything between and including the brackets
  const cleanJson = cleanedText.substring(firstBracket, lastBracket + 1);
  
  logger.log('🔧 EXTRACTING CLEAN JSON - Extracted JSON length:', cleanJson.length);
  logger.log('🔧 EXTRACTING CLEAN JSON - Clean JSON content:', cleanJson);
  
  // ENHANCED: Check if the extracted JSON contains Form object
  if (cleanJson.includes('"Form"')) {
    logger.log('🔧 EXTRACTING CLEAN JSON - ✅ Form object detected in extracted JSON!');
  } else {
    logger.log('🔧 EXTRACTING CLEAN JSON - ❌ No Form object found in extracted JSON');
  }
  
  return cleanJson;
};

// Helper function to strip markdown code blocks and clean JSON
const stripMarkdownAndCleanJSON = (text: string): string => {
  logger.log('🔧 STRIPPING MARKDOWN - Original text (first 200 chars):', text.substring(0, 200));
  
  // Remove markdown code blocks (```json ... ``` or ``` ... ```)
  let cleaned = text.replace(/```(?:json)?\s*([\s\S]*?)\s*```/g, '$1');
  
  // If no code blocks were found, try to extract JSON from the text
  if (cleaned === text) {
    // Look for JSON-like content between { and }
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      cleaned = jsonMatch[0];
    }
  }
  
  // Trim whitespace
  cleaned = cleaned.trim();
  
  logger.log('🔧 STRIPPING MARKDOWN - Cleaned text (first 200 chars):', cleaned.substring(0, 200));
  
  return cleaned;
};

// Helper function to convert Reply_Options to reply_options format
export const convertReplyOptionsToArray = (replyOptions: any): string[] => {
  if (Array.isArray(replyOptions)) {
    // Handle array format: [{ Option: "value" }, { option: "value" }]
    return replyOptions.map((item: any) => item.Option || item.option).filter(Boolean);
  } else if (typeof replyOptions === 'object' && replyOptions) {
    // Handle object format: { Option1: "value", Option2: "value" }
    const optionKeys = Object.keys(replyOptions).filter(key => 
      key.toLowerCase().startsWith('option') || key === 'Option' || key === 'option'
    );
    
    // Sort keys to ensure proper order (Option1, Option2, Option3, etc.)
    const sortedKeys = optionKeys.sort((a, b) => {
      const numA = parseInt(a.replace(/[^\d]/g, '')) || 0;
      const numB = parseInt(b.replace(/[^\d]/g, '')) || 0;
      return numA - numB;
    });
    
    return sortedKeys.map(key => replyOptions[key]).filter(Boolean);
  }
  return [];
};

// Helper function to get similar_vehicles URL from response (case-insensitive)
export const getSimilarVehiclesUrl = (response: any): string | null => {
  // Check for both variations: Similar_Vehicles and similar_vehicles
  if (response?.Similar_Vehicles) {
    return response.Similar_Vehicles;
  }
  if (response?.similar_vehicles) {
    return response.similar_vehicles;
  }
  return null;
};

export const extractJSONFromMixedContent = (aiResponseText: string): AIResponse | null => {
  // ENHANCED: More comprehensive regex patterns to catch Form.Type
  const patterns = [
    // Original pattern
    /\{[^{}]*(?:"Reply_Message"|"Form"|"FormTitle"|"reply_options"|"Reply_Options"|"Contact"|"Interest")[^{}]*\}/g,
    // Pattern for Form.Type specifically
    /\{[^{}]*["']?Form\.?Type["']?\s*:[^{}]*\}/gi,
    // Pattern for nested Form objects
    /\{[^{}]*["']?Form["']?\s*:\s*\{[^{}]*["']?Type["']?[^{}]*\}[^{}]*\}/gi
  ];
  
  for (const regex of patterns) {
    let jsonMatch;
    regex.lastIndex = 0; // Reset regex state
    
    while ((jsonMatch = regex.exec(aiResponseText)) !== null) {
      try {
        let jsonString = jsonMatch[0];
        
        // Clean up the JSON string
        jsonString = jsonString.replace(/,(\s*\})/g, '$1'); // Remove trailing commas
        jsonString = jsonString.replace(/(\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":'); // Quote property names
        jsonString = jsonString.replace(/Form\.Type/gi, '"Form": {"Type"'); // Handle Form.Type syntax
        
        const parsed = JSON.parse(jsonString);
        if (parsed.Form || parsed.Reply_Message || parsed.reply_options || parsed.Reply_Options || parsed.Contact || parsed.Interest) {
          logger.log('Extracted JSON from mixed content with enhanced patterns:', parsed);
          return parsed;
        }
      } catch (error) {
        logger.log('Failed to parse extracted JSON with pattern:', error);
      }
    }
  }
  
  return null;
};

export const parseAIResponse = (aiResponseText: string): AIResponse | null => {
  logger.log('🔧🔧🔧 PARSE AI RESPONSE - FUNCTION CALLED! Starting with text (first 200 chars):', aiResponseText.substring(0, 200));
  logger.log('🔧🔧🔧 PARSE AI RESPONSE - Contains Form?', aiResponseText.includes('"Form"'));
  logger.log('🔧🔧🔧 PARSE AI RESPONSE - Contains Type?', aiResponseText.includes('"Type"'));
  logger.log('🔧🔧🔧 PARSE AI RESPONSE - Starting with text (first 200 chars):', aiResponseText.substring(0, 200));
  
  // ENHANCED: Add better pattern matching for common AI responses that should trigger contact forms
  const hasVehicleListingPattern = /\b(?:here\s+(?:are|is)|found|available|inventory|vehicles?|cars?|models?)\b.*\b(?:you\s+can|available|for\s+sale|in\s+stock|interested)\b/i;
  const shouldTriggerContactForm = hasVehicleListingPattern.test(aiResponseText) && 
                                  !aiResponseText.includes('"Form"') && 
                                  !aiResponseText.includes('Form.Type');
  
  if (shouldTriggerContactForm) {
    logger.log('🔧 PARSE AI RESPONSE - Vehicle listing detected without Form field, attempting to parse and add Form');
    try {
      const cleanJson = extractCleanJSON(aiResponseText);
      const parsed = JSON.parse(cleanJson);
      // Add Form field to the parsed object
      const syntheticResponse: AIResponse = {
        ...parsed,
        Form: {
          Type: 'Contact',
          Title: 'Contact Us'
        }
      };
      logger.log('🔧 PARSE AI RESPONSE - Created synthetic Contact form response with parsed data');
      return deduplicateYouTubeVideos(syntheticResponse);
    } catch (e) {
      logger.log('🔧 PARSE AI RESPONSE - Failed to parse for synthetic response, skipping:', e);
      // Don't create synthetic response if parsing fails
    }
  }
  
  try {
    // STEP 1: Extract clean JSON by finding first { and last }
    const cleanJson = extractCleanJSON(aiResponseText);
    
    // STEP 2: Try to parse the clean JSON
    const rawParsed = JSON.parse(cleanJson);
    
    // STEP 2.1: Normalize all keys to handle PascalCase/camelCase variations from AI
    const parsed = normalizeAIResponse(rawParsed);
    logger.log('🔧🔧🔧 PARSE AI RESPONSE - Successfully parsed and normalized JSON:', parsed);
    logger.log('🔧🔧🔧 PARSE AI RESPONSE - Form field in parsed response:', parsed.Form);
    
    // STEP 2.5: Check for empty inventory and send alert if detected
    // Only alert if the AI returned empty placeholder objects but DIDN'T explain that inventory is unavailable
    const inventory = parsed?.Reply_Message?.Content?.Inventory;
    if (Array.isArray(inventory) && inventory.length > 0) {
      // Check if all inventory items have empty VIN and Exterior_Color
      const hasEmptyInventory = inventory.every((item: any) => {
        const vin = item?.VIN || '';
        const color = item?.Exterior_Color || item?.Vehicle_Color || '';
        return vin.trim() === '' && color.trim() === '';
      });
      
      if (hasEmptyInventory) {
        // Check if AI explicitly mentioned no inventory - this is intentional, not an error
        const intro = parsed?.Reply_Message?.Intro || '';
        const closing = parsed?.Reply_Message?.Closing || '';
        const combinedText = (intro + ' ' + closing).toLowerCase();
        
        // Phrases indicating the AI intentionally reported no inventory
        const noInventoryPhrases = [
          'no inventory', 'aucun inventaire', 'pas en inventaire', 
          'not in stock', 'pas en stock', 'aucune en inventaire',
          'no vehicles', 'aucun véhicule', 'none available',
          'pas disponible', 'not available', 'aucune disponible',
          'sur commande', 'on order', 'commande spéciale',
          'aucune', 'ne vois aucun', 'je ne vois aucune'
        ];
        
        const aiKnowsNoInventory = noInventoryPhrases.some(phrase => 
          combinedText.includes(phrase)
        );
        
        if (aiKnowsNoInventory) {
          logger.log('📋 Empty inventory objects detected but AI correctly explained no inventory available - not alerting');
        } else {
          // Deduplicate alerts - only send once per unique response
          const alertKey = `empty-inv-alert-${btoa(aiResponseText.substring(0, 200)).replace(/[^a-zA-Z0-9]/g, '').substring(0, 50)}`;
          const alreadySent = sessionStorage.getItem(alertKey);
          
          if (alreadySent) {
            logger.log('📋 Empty inventory alert already sent for this response - skipping duplicate');
          } else {
            logger.error('🚨 EMPTY INVENTORY DETECTED - AI returned empty objects without explaining why');
            sessionStorage.setItem(alertKey, 'true');
            
            // Send empty inventory alert email (don't await to avoid blocking)
            SecureStorage.getItem('dealerData').then((dealerData: any) => {
              const sessionId =
                sessionManager.getCurrentSession()?.sessionId ||
                sessionStorage.getItem('session-id') ||
                'Unknown';

              const userQuestion = getLastUserQuestionFromSessionStorage() || 'Not available';
              
              supabase.functions.invoke('send-empty-inventory-alert', {
                body: {
                  question: userQuestion,
                  rawAIResponse: aiResponseText,
                  dealerId: dealerData?.DealerID,
                  sessionId,
                  timestamp: new Date().toISOString(),
                  emptyInventoryData: inventory,
                  userAgent: navigator.userAgent
                }
              }).catch(err => {
                console.error('Failed to send empty inventory alert:', err);
              });
            }).catch(err => {
              console.error('Error preparing empty inventory alert:', err);
            });
          }
        }
      }
    }
    
    // STEP 3: Validate the parsed response
    logger.log('🔧 PARSE AI RESPONSE - About to validate parsed response:', parsed);
    const validation = validateOpenAIResponse(parsed);
    logger.log('🔧 PARSE AI RESPONSE - Validation result:', validation);
    
    if (!validation.isValid) {
      logger.error('🚨 Response validation failed:', validation.error);
      logger.log('🔧 PARSE AI RESPONSE - Returning parsed response anyway for vehicles check');
      // Don't throw error, return the parsed response so we can check for vehicles
      return deduplicateYouTubeVideos(parsed);
    }
    
    return deduplicateYouTubeVideos(parsed);
  } catch (parseError) {
    logger.log('🔧 PARSE AI RESPONSE - Clean JSON parsing failed:', parseError);
    
    // STEP 3: Fallback to markdown stripping
    try {
      const cleanedText = stripMarkdownAndCleanJSON(aiResponseText);
      const parsed = JSON.parse(cleanedText);
      logger.log('🔧 PARSE AI RESPONSE - Markdown stripping successful:', parsed);
      
      // Validate the parsed response
      const validation = validateOpenAIResponse(parsed);
      if (!validation.isValid) {
        logger.error('🚨 Response validation failed (markdown fallback):', validation.error);
        handleInvalidResponse(validation.error || 'Unknown validation error');
        throw new Error(createInvalidResponseError());
      }
      
      return deduplicateYouTubeVideos(parsed);
      } catch (markdownError) {
        logger.log('🔧 PARSE AI RESPONSE - Markdown stripping also failed:', markdownError);
        
        // STEP 4: Final fallback - try parsing original text as-is
        try {
          const parsed = JSON.parse(aiResponseText);
          logger.log('🔧 PARSE AI RESPONSE - Original text parsing successful:', parsed);
          
          // Validate the parsed response
          const validation = validateOpenAIResponse(parsed);
          if (!validation.isValid) {
            logger.error('🚨 Response validation failed (original text fallback):', validation.error);
            logger.log('🔧 PARSE AI RESPONSE - Returning parsed response anyway for vehicles check');
            // Don't throw error, return the parsed response so we can check for vehicles
            return deduplicateYouTubeVideos(parsed);
          }
          
          return deduplicateYouTubeVideos(parsed);
        } catch (originalError) {
          logger.log('🔧 PARSE AI RESPONSE - All JSON parsing attempts failed:', originalError);
          
          // Don't create synthetic responses with raw strings - this causes parsing issues
          // If we can't parse the JSON, just return null and let the error handling take over
          
          logger.log('🔧 PARSE AI RESPONSE - No Form.Type found anywhere, returning null');
          return null;
        }
      }
  }
};
